import PropTypes from 'prop-types';
import { useRef } from 'react';

import useSize from '../../../utils/hooks/useSize';

import { analytics, getClickCtaDetail } from '../../../utils/analytics';
import { isDDEnv, getContent } from '../../../utils/helper';

import CtaCtf from '../../molecules/CTA';
import { DynamicAsset } from '../../molecules/Media/Media';
import RichText from '../../molecules/RichText/RichText';
import Pill from '../../atoms/Pill/Pill';

import {
  CtfCtaStyle,
  CtfAssetPositions,
  CtfWidthOptions,
} from '../../../utils/constants';
import { TEXT_BASE_CLASS } from '../../atoms/Text/Text.constants';

function StoryNormalDD({ assets = [], compactSpace, entries = [], fields = {} }) {
  const storyRef = useRef(null);
  const size = useSize(storyRef);
  // @TODO: Improve variable spreading and type checking.
  const {
    id,
    title,
    text,
    callToAction,
    superText,
    headingWeight,
    dynamicAsset,
    assetPosition,
    width,
    hasBackground,
    hasRoundedAsset,
    hideText,
    hideHeader,
    cloudinaryAssets,
    mobileCloudinaryAsset,
    removeYPadding,
    tag: contentTag,
  } = fields;
  const HeadingTag = headingWeight ? headingWeight.toLowerCase() : 'h3';
  const altCase = width === CtfWidthOptions.FULL_WIDTH && hasBackground;
  const tag = contentTag?.sys?.id
    ? getContent(contentTag?.sys?.id, entries)
    : null;
  const mobileAsset = mobileCloudinaryAsset ?? cloudinaryAssets;

  return (
    <div
      className={`aaco-story ${!compactSpace && !removeYPadding && 'mb-20'}`}
      id={id || title.toLowerCase().replace(' ', '-')}
    >
      <div
        className={`flex flex-wrap items-center ${
          width !== CtfWidthOptions.FULL_WIDTH &&
          !compactSpace &&
          'container mx-auto'
        } ${width === CtfWidthOptions.NO_LEFT && 'ml-0 pl-0'} ${
          width === CtfWidthOptions.NO_RIGHT && 'mr-0 pr-0'
        } ${
          altCase &&
          !compactSpace &&
          'md:bg-majesty md:dd-kr:bg-progressive-blue md:dd-en:bg-progressive-blue md:mr-[15em] mt-20'
        } ${
          hasBackground
            ? 'dd-en:bg-progressive-blue dd-kr:bg-progressive-blue'
            : ''
        } ${
          width === CtfWidthOptions.PADDING &&
          hasBackground &&
          'dd-en:rounded-[8px] dd-kr:mx-[30px]'
        }`}
        ref={(dynamicAsset || cloudinaryAssets) && storyRef}
      >
        <div
          className={`aaco-story__image mb-8 dd-kr:mb-0 dd-en:mb-0 md:mb-0 min-h-full w-full md:hidden ${
            !compactSpace && 'md:w-1/2'
          } ${
            !compactSpace &&
            (assetPosition === CtfAssetPositions.LEFT
              ? 'md:order-1'
              : 'md:order-3')
          } ${
            altCase &&
            !compactSpace &&
            'md:relative md:right-[-8em] md:top-[-5em] md:max-w-[45%]'
          }`}
        >
          {(mobileAsset || dynamicAsset) && (
            <DynamicAsset
              assets={assets}
              callToAction={callToAction}
              controls={false}
              entries={entries}
              field={mobileAsset ?? dynamicAsset}
              size={size}
              lightbox
              container={
                width !== CtfWidthOptions.PADDING ? 'half' : 'half-container'
              }
              style={`${
                hasRoundedAsset
                  ? width === CtfWidthOptions.FULL_WIDTH
                    ? 'dd-en:rounded-none dd-kr:rounded-none md:dd-en:rounded-[8px] md:dd-kr:rounded-[8px]'
                    : 'dd-en:rounded-[8px] dd-kr:rounded-[8px]'
                  : ''
              }`}
            />
          )}
        </div>
        <div
          className={`aaco-story__image mb-8 dd-kr:mb-0 dd-en:mb-0 md:mb-0 min-h-full w-full hidden md:block ${
            !compactSpace && 'md:w-1/2'
          } ${
            !compactSpace &&
            (assetPosition === CtfAssetPositions.LEFT
              ? 'md:order-1'
              : 'md:order-3')
          } ${
            altCase &&
            !compactSpace &&
            'md:relative md:right-[-8em] md:top-[-5em] md:max-w-[45%]'
          }`}
        >
          {(cloudinaryAssets || dynamicAsset) && (
            <DynamicAsset
              assets={assets}
              callToAction={callToAction}
              controls={false}
              entries={entries}
              field={cloudinaryAssets ?? dynamicAsset}
              size={size}
              lightbox
              container={
                width !== CtfWidthOptions.PADDING ? 'half' : 'half-container'
              }
              style={`${
                hasRoundedAsset
                  ? width === CtfWidthOptions.FULL_WIDTH
                    ? 'dd-en:rounded-none dd-kr:rounded-none md:dd-en:rounded-[8px] md:dd-kr:rounded-[8px]'
                    : 'dd-en:rounded-[8px] dd-kr:rounded-[8px]'
                  : ''
              }`}
            />
          )}
        </div>
        {!hideText && (
          <div
            className={`aaco-story__content min-h-full order-2
            p-8 dd-kr:pt-[25px] dd-en:pt-[25px] md:dd-kr:py-8 md:dd-en:py-8 ${
              !compactSpace && ' md:w-1/2 md:px-16'
            } dd-kr:px-[30px] dd-en:px-[30px] dd-kr:md:px-[100px] dd-en:md:px-[100px] w-full 2xl:max-w-half ${
              hasBackground
                ? 'bg-majesty text-white self-stretch dd-kr:bg-progressive-blue dd-en:bg-progressive-blue'
                : 'text-grey-6 self-center dd-kr:pb-0 dd-en:pb-0'
            } ${
              !compactSpace &&
              altCase &&
              'md:flex md:flex-col md:justify-center md:alt 2xl:ml-[]'
            }`}
          >
            {superText && (
              <h6
                className={`mb-4 text-tag uppercase ${
                  hasBackground
                    ? 'text-white dd-en:80-white-on-dark-blue dd-kr:80-white-on-dark-blue'
                    : 'text-violet dd-en:text-grey-6 dd-kr:text-grey-6'
                } ${!compactSpace && !isDDEnv() && 'md:ml-1/3'}
              `}
              >
                {superText}
              </h6>
            )}
            {tag && (
              <Pill
                style="pill-action mb-6 inline-flex items-center cursor-default"
                small
              >
                {tag.fields?.name}
              </Pill>
            )}
            {title && !hideHeader && (
              <HeadingTag
                className={`mb-6 ${
                  isDDEnv() ? TEXT_BASE_CLASS.h4 : TEXT_BASE_CLASS.h3
                } ${
                  hasBackground
                    ? 'text-white dd-en:text-white dd-kr:text-white'
                    : 'text-majesty dd-en:text-progressive-blue dd-kr:text-progressive-blue'
                } ${
                  !compactSpace &&
                  'md:mb-4 md:dd-kr:mb-[15px] md:dd-en:mb-[15px]'
                }`}
              >
                {title}
              </HeadingTag>
            )}
            <div
              className={`aaco-story__inner-content ${!isDDEnv() && 'ml-1/3'}`}
            >
              {text && !text.content && (
                <p
                  className={`text-body ${
                    hasBackground
                      ? 'dd-en:80-white-on-dark-blue dd-kr:80-white-on-dark-blue'
                      : 'dd-en:text-grey-6 dd-kr:text-grey-6'
                  } ${!isDDEnv() && 'text-black'} md:text-white`}
                >
                  {text}
                </p>
              )}
              {text &&
                text.content &&
                text.content.length > 0 &&
                text.content.map(({ nodeType, content }, index) => (
                  <RichText
                    entries={entries}
                    nodeType={nodeType}
                    content={content}
                    key={index}
                    richTextStyle={{
                      paragraph: {
                        customFont:
                          TEXT_BASE_CLASS['body'] +
                          (hasBackground
                            ? ' text-majesty-50 dd-en:text-80-white-on-dark-blue dd-kr:text-80-white-on-dark-blue'
                            : ' dd-en:text-grey-6 dd-kr:text-grey-6') +
                          ' font-text',
                        blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                      },
                    }}
                  />
                ))}
              {callToAction && (
                <div
                  className={`aaco-story__cta pt-3 dd-kr:md:pt-5 dd-en:md:pt-5 ${
                    hasBackground ? 'text-white' : 'text-black'
                  }`}
                >
                  <CtaCtf
                    type={
                      isDDEnv() ? CtfCtaStyle.DD_SECONDARY : CtfCtaStyle.PRIMARY
                    }
                    field={callToAction}
                    entries={entries}
                    invert={hasBackground}
                    onClick={(e, { localUrl, url, name }) =>
                      analytics({
                        window,
                        document,
                        detail: getClickCtaDetail(e, { localUrl, url, name }),
                      })
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// @TODO: define real props for the Story based on it requirements
StoryNormalDD.propTypes = {
  assets: PropTypes.array,
  compactSpace: PropTypes.bool,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default StoryNormalDD;
